import { Percent, Plus, Settings, X } from "@tamagui/lucide-icons";
import Big from "big.js";
import { observer } from "mobx-react-lite";
import {
  type PropsWithChildren,
  useCallback,
  useEffect,
  useState,
} from "react";
import { Platform } from "react-native";
import { Popover, type PopoverProps, View, XStack, YStack } from "tamagui";
import { Body } from "../../../../ui/Body";
import { Input } from "../../../../ui/InputNew";
import { Label } from "../../../../ui/Label";
import { Subtext } from "../../../../ui/Subtext";
import { UserAvatar } from "../../../../ui/UserAvatar";
import type UserModel from "../../../../users/UserModel";
import type ExpenseModuleModel from "../../ExpenseModuleModel";
import type { CalculatorState } from "../Calculator";
import type { ExpenseFormState } from "./ExpenseFormState";

function getPercentageFromSplit(
  splitAmount: Big | undefined,
  totalAmount: Big,
) {
  if (!splitAmount || splitAmount?.lte(0) || totalAmount.lte(0)) {
    return "0";
  }

  return splitAmount && totalAmount
    ? `${Math.round(splitAmount.div(totalAmount).toNumber() * 100)}`
    : "0";
}

export const PopoverPercentage: React.FC<
  PropsWithChildren<
    PopoverProps & {
      userId: string;
      amount?: Big;
      formState: ExpenseFormState;
      onPress: () => void;
    }
  >
> = ({ children, userId, amount, formState, onPress, ...props }) => {
  const [value, setValue] = useState(
    getPercentageFromSplit(amount, formState.amount),
  );

  useEffect(() => {
    setValue(getPercentageFromSplit(amount, formState.amount));
  }, [amount, formState]);

  const setPercentage = useCallback(() => {
    const percentage = Number(value);

    if (Number.isNaN(percentage)) {
      return;
    }

    const decimal = percentage / 100;
    const newValue = formState.amount.times(decimal);

    formState.setSplit(userId, newValue);
  }, [value, userId, formState]);

  return (
    <Popover size="$medium" allowFlip placement="bottom-end" {...props}>
      <Popover.Trigger
        onPress={(e) => {
          e.stopPropagation();
          onPress();
        }}
        cursor="pointer"
      >
        {children}
      </Popover.Trigger>

      <Popover.Content
        borderWidth={1}
        borderColor="$divider"
        enterStyle={{ y: -10, opacity: 0 }}
        exitStyle={{ y: -10, opacity: 0 }}
        padding={0}
        borderRadius={0}
        backgroundColor={"$backgroundBlock"}
        shadowColor={"#000"}
        shadowOpacity={0.15}
        shadowRadius={24}
        shadowOffset={{ height: 0, width: 0 }}
        animation={[
          "quick",
          {
            opacity: {
              overshootClamping: true,
            },
          },
        ]}
        onPress={(e) => {
          e.stopPropagation();
        }}
      >
        <Popover.Arrow
          backgroundColor={"$backgroundBlock"}
          borderWidth={1}
          borderColor="$divider"
        />
        <YStack gap="$space.medium" padding="$space.medium">
          <Input
            type="border"
            multiline={false}
            keyboardType="numeric"
            onChangeText={(text) => setValue(text)}
            value={value}
          />
        </YStack>
        <Popover.Close asChild>
          <XStack
            borderTopWidth={1}
            borderColor={"$divider"}
            width="100%"
            alignItems="center"
            cursor="pointer"
          >
            <XStack
              padding="$space.medium"
              justifyContent="center"
              flex={1}
              alignItems="center"
              gap="$small"
              backgroundColor={"$primary"}
              onPress={setPercentage}
            >
              <Percent size="$small" />
              <Label>Set percentage</Label>
            </XStack>
            <View padding="$space.medium" flex={1}>
              <Body textAlign="center">Cancel</Body>
            </View>
          </XStack>
        </Popover.Close>
      </Popover.Content>
    </Popover>
  );
};

export function scrollIntoViewWithoutOverlay(
  element: HTMLElement,
  padding: number,
) {
  setTimeout(() => {
    const rect = element.getBoundingClientRect();
    const fixedDivHeight = 281; // Set this to the height of your fixed div

    if (rect.bottom > window.innerHeight - fixedDivHeight) {
      // If the element is under the fixed div, scroll it up
      window.scrollTo({
        top:
          window.scrollY +
          rect.top -
          (window.innerHeight - fixedDivHeight) +
          rect.height +
          padding,
        behavior: "smooth",
      });
    } else if (rect.top < 0) {
      // If the element is above the view, scroll it into view
      window.scrollTo({
        top: window.scrollY + rect.top + padding,
        behavior: "smooth",
      });
    }
  }, 100);
}

export const AddExpenseFormSplitRow: React.FC<{
  module: ExpenseModuleModel;
  calculatorState: CalculatorState;
  formState: ExpenseFormState;
  user: UserModel;
}> = observer(({ module, user, calculatorState, formState }) => {
  const inputKey = `split-${user.uid}`;
  const split = formState.splits.find((split) => split.userId === user.uid);
  const selected = calculatorState.inputKey === inputKey;
  const hasAmount = new Big(split?.amount ?? 0).gt(0);

  return (
    <XStack
      alignItems="center"
      justifyContent="space-between"
      padding="$large"
      backgroundColor={"$backgroundBlock"}
      outlineColor={"$primary"}
      outlineWidth={selected ? 1 : 0}
      outlineStyle="solid"
      outlineOffset={-2}
      opacity={selected || hasAmount ? 1 : 0.5}
      onPress={(e) => {
        e.stopPropagation();

        if (Platform.OS === "web") {
          const element = e.target as unknown as HTMLDivElement;

          scrollIntoViewWithoutOverlay(element, 40);
        }

        if (selected) {
          calculatorState.setExpanded(false);
          return;
        }

        calculatorState.setExpanded(
          true,
          inputKey,
          String(Number.parseFloat(split?.amount.prec(6).toFixed(4) ?? "0")),
          (value, _readable) => {
            if (value !== undefined && value !== (split?.amount ?? 0)) {
              formState.setSplit(user.uid, value);
            }
          },
        );
      }}
    >
      <XStack gap={"$medium"} alignItems="center">
        <UserAvatar userUid={user.uid} />
        <Body>{user.data.displayName}</Body>
      </XStack>
      <XStack gap={"$larger"} alignItems="center">
        <View alignItems="flex-end">
          <Body>
            {selected
              ? (calculatorState.readable ?? "0")
              : module.currencyFormatter.format(
                  formState.splits
                    .find((split) => split.userId === user.uid)
                    ?.amount.toNumber() ?? 0,
                )}{" "}
          </Body>
          <PopoverPercentage
            formState={formState}
            userId={user.uid}
            amount={split?.amount}
            onPress={() => {
              if (selected) {
                calculatorState.setExpanded(false);
                return;
              }
            }}
          >
            <XStack cursor="pointer" alignItems="center">
              <Subtext>
                {getPercentageFromSplit(split?.amount, formState.amount)}%{" "}
              </Subtext>
              <Settings size={12} />
            </XStack>
          </PopoverPercentage>
        </View>
        <View
          height={32}
          width={32}
          alignItems="flex-end"
          justifyContent="center"
          onPress={(e) => {
            e.stopPropagation();

            calculatorState.setExpanded(false);

            if (!hasAmount) {
              formState.setSplit(user.uid);
              return;
            }

            formState.removeSplit(user.uid);
          }}
        >
          {hasAmount ? <X /> : <Plus />}
        </View>
      </XStack>
    </XStack>
  );
});
